import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { css } from '@emotion/css';
import { Button } from './components/styles/SmbButton.styled';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import type { Address, Utilities } from './types';
import { fetchAddress, fetchUtility } from './api/api';
import { Dropdown, Modal } from 'react-bootstrap';
import { BillReaderModal } from './components/bill-reader-modal';
import { ChooseDataSourceModal } from './components/choose-data-source-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { ContactModalBody, StyledModal } from "./components/styles/Smb.styled";

export const ValidateAddress = ({ onsubmit }: { onsubmit: () => void }) => {
    const { t } = useTranslation('homepage');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [addressOptions, setAddressOptions] = useState<Address[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<Address[]>([]);
    const [showDataSourceModal, setShowDataSourceModal] = useState(false);
    const [cookies, setCookie, removeCookie] = useCookies(['consent_id']);
    const [loadingUtility, setLoadingUtility] = useState(false);
    const [utility, setUtility] = useState<Utilities[]>([]);
    const [selectedUtility, setSelectedUtility] = useState<Utilities | undefined>(undefined);
    const [showBillReaderModal, setShowBillReaderModal] = useState(false);
    const [showHomeModal, setShowHomeModal] = useState(false);


    useEffect(() => {
        const savedAddress = localStorage.getItem('saved_address');
        if (savedAddress) {
            fetchAddress(savedAddress).then(data => {
                if (data && data.length > 0) {
                    setSelectedAddress(data);
                }
            })
        }
    }, []);

    useEffect(() => {
        if (selectedAddress.length === 0) return;
        setLoadingUtility(true);
        const { zipcode } = selectedAddress[0];
        fetchUtility(zipcode).then(res => {
            setLoadingUtility(false);
            if (res) {
                setUtility(res);
                setSelectedUtility(res[0]);
            }
        })
    }, [selectedAddress])

    const handleSearch = (val: string) => {
        setLoading(true);
        fetchAddress(val).then(data => {
            setAddressOptions(data);
            setLoading(false);
        })
    }

    const handleClick = () => {
        if (selectedAddress.length === 0) return;
        const address = selectedAddress[0];
        const { street, esiid, zipcode, city, state } = address;
        const utilityCode = selectedUtility ? selectedUtility.utility_code : '';
        if (address.premise_type === 'Residential') {
            setShowHomeModal(true)

        } else {
            onsubmit()

        }
        localStorage.setItem('saved_address', address.street);
        // setShowDataSourceModal(true);
    }

    const redirectTo = (type: string) => {
        const address = selectedAddress[0];
        const { street, esiid, zipcode, city, state } = address;
        const utilityCode = selectedUtility ? selectedUtility.utility_code : '';
        if (type === 'plan') {
            if (cookies) {
                removeCookie('consent_id');
            }
            navigate(`/plans?address=${encodeURIComponent(street)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&utcode=${utilityCode}`);
        }
        else {
            navigate(`/get_usage?address=${encodeURIComponent(street)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&source=${type}&utcode=${utilityCode}`);
        }
    }
    const onHomeSubmit = () => {
        if (selectedAddress.length === 0) return;
        const address = selectedAddress[0];
        const { street, esiid, zipcode, city, state } = address;
        const utilityCode = selectedUtility ? selectedUtility.utility_code : '';
        navigate(`/plans?address=${encodeURIComponent(street)}&esiid=${esiid}&zipcode=${zipcode}&city=${city}&state=${state}&utcode=${utilityCode}`);
    }

    const chooseUtility = () => {
        if (utility.length <= 1 || !selectedUtility) return null;
        return (
            <Dropdown className="utility-select">
                <Dropdown.Toggle variant="light">
                    {selectedUtility.utility_name}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {
                        utility.map((val, idx) => (
                            <Dropdown.Item
                                eventKey={idx}
                                key={idx}
                                onClick={() => {
                                    setSelectedUtility(val);
                                }}
                                active={val.utility_id === selectedUtility.utility_id}
                            >{val.utility_name}</Dropdown.Item>
                        ))
                    }
                </Dropdown.Menu>
            </Dropdown>
        )
    }
    const responsiveStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

    const autocompleteWrapperStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

    const buttonWrapperStyles = css`
  @media (max-width: 768px) {
    width: 100%;
  }
`;
    return (
        <div className={responsiveStyles}>
            <div className={autocompleteWrapperStyles}>
                <div id="autocomplete-wrapper" className={css`
            position: relative;
            width: 100%;
            
            @media (max-width: 768px) {
                margin-bottom: 5px;
            }
        `}>
                    <FontAwesomeIcon
                        icon={faMapMarkerAlt as any}
                        className="me-3 position-absolute"
                        style={{
                            fontSize: 20,
                            color: '#aaa',
                            left: 16,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10
                        }}
                    />
                    <AsyncTypeahead
                        filterBy={() => true}
                        id="autocomplete"
                        isLoading={loading}
                        labelKey="label"
                        minLength={1}
                        onSearch={handleSearch}
                        options={addressOptions}
                        placeholder={`Enter your business address…`}
                        size="lg"
                        onChange={setSelectedAddress}
                        selected={selectedAddress}
                        className={css`
                    & .rbt-input-main {
                        padding-left: 50px !important;
                        
                        @media (max-width: 768px) {
                            height: 45px;
                            font-size: 16px;
                        }
                    }
                `}
                    />
                </div>
                {chooseUtility()}
            </div>
            <div className={buttonWrapperStyles}>
                <Button
                    onClick={handleClick}
                    disabled={!selectedAddress[0] || loadingUtility}
                    className={css` 
                display: block;
                margin-left: auto;
                min-height: 45px;
                font-size: 16px;
                padding: 10px 20px;
                
                @media (max-width: 768px) {
                    margin-left: auto; 
                    margin-right: 0;
                }
            `}
                >
                    <span>Get Started</span>
                </Button>
            </div>
            <BillReaderModal
                show={showBillReaderModal}
                handleClose={() => { setShowBillReaderModal(false); }}
            />
            <ChooseDataSourceModal
                show={showDataSourceModal}
                handleClose={() => { setShowDataSourceModal(false); }}
                handleRedirect={redirectTo}
                utility={selectedUtility}
            />

            <StyledModal
                size="lg"
                show={showHomeModal}
                style={{ marginTop: "150px", }}
                onHide={() => {
                    setShowHomeModal(false)
                }}
            >
                <div style={{ backgroundColor: "#015343", color: "white", borderRadius: "12px", }}>
                    <Modal.Header closeButton>
                        <Modal.Title >Shopping for Residential Energy Plans?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ContactModalBody>
                            <>
                                <div style={{ textAlign: "left", display: "flex", alignItems: "start", gap: "14px", }}>
                                    <img src="/images/icon-home.png" width={26} className="mt-1 " alt="" />
                                    <p>
                                        Looks like your address is registered as residential.<br />We’ll redirect you to the best plans for your home.
                                    </p>
                                </div>
                                <div style={{
                                    display: "flex",
                                    flexDirection: "row-reverse",
                                    gap: "10px",
                                    alignItems: "start",
                                    marginTop: "64px",
                                }}
                                    className={css`
  @media (max-width: 768px) {
    flex-direction: column !important;
    align-items: center !important;
  }
`}>
                                    <Button
                                        className={css`
      @media (max-width: 768px) {
        width: 100%;
        max-width: 300px;
      }
      @media (min-width: 769px) {
        width: auto;
      }
    `}
                                        onClick={() => { onHomeSubmit() }}
                                    >
                                        SEE RESIDENTIAL PLANS
                                    </Button>
                                    <Button
                                        className={css`
      @media (max-width: 768px) {
        width: 100%;
        max-width: 300px;
      }
      @media (min-width: 769px) {
        width: auto;
      }
    `}
                                        variant="secondary"
                                        onClick={() => { setShowHomeModal(false) }}
                                    >
                                        CHANGE ADDRESS
                                    </Button>
                                </div>
                            </>


                        </ContactModalBody>
                    </Modal.Body>
                </div>
            </StyledModal>
        </div>
    )
}